import { apiSlice } from './apiSlice'

export interface PurchaseContractPayload {
  contractNo: string
  contractDate: string
  supplierCode: string
  supplierName: string
  sellersRef: string
  nptRef: string
  noOfBags: number | null
  qtyKg: number | null
  certification: string
  licenseCertNo: string
  si: string
  sd: string
  meOrIb: string
  contractType: string
  packageCode: string
  packageName: string
  priceUnit: string
  outrightPrice: number | null
  ptbfDiffs: string
  applicableVat: boolean
  vatRate: string
  fx: string
  futuresMonth: string
  fixationCall: string
  latestDelivery: string
  deliveryTerm: string
  deliveryPlace: string
  deliveryPlaceName: string
  logisticCost: string
  note: string
  traderInCharge: string
  fixedFxIfHave: string
  provOnMarketPrice: number | null
  stoplossRate: number | null
  depositValuePct: number | null
  depositLimitPct: number | null
  depositBonusPct: number | null
  interestPctPerMonth: number | null
  timeLimit: string
  repaymentDate: string
  intermediary: boolean
  issued: boolean
  sentMail: boolean
  receivedMail: boolean
  sentPaper: boolean
  receivedPaper: boolean
  shipperCode: string
  shipperName: string
  contractRef: string
  contractShipperIntermediary: boolean
  outrightShipperIntermediary: boolean
  ptbfdiffsShipperIntermediary: boolean
}

export interface PurchaseContract extends PurchaseContractPayload {
  id: string
  status: 'Draft' | 'Cancelled'
  createdAt: string
  updatedAt: string
  createdBy: string
  rawPurchaseSheetRow?: Record<string, string | null>
}

export interface PurchaseContractListResponse {
  items: PurchaseContract[]
  total: number
  page: number
  pageSize: number
}

export interface PurchaseContractListQuery {
  page: number
  pageSize: number
  q?: string
  status?: string
  supplierCode?: string
  contractType?: string
  certification?: string
}

export interface PurchaseContractPagePreferences {
  deliveryLatestFrom: string
  deliveryLatestTo: string
  isForcedClose: 'all' | 'yes' | 'no'
}

export interface PurchaseContractColumnsPreference {
  columns: string[] | null
  defaultColumns: string[] | null
}

export type PurchaseContractColumnsMode =
  | 'pucontract'
  | 'forward-pu'
  | 'forward-list'
  | 'npe-contract'
  | 'contracts-all'
  | 'annex'
  | 'ptbf-fixation'
  | 'npe-fixation'
  | 'npe-allocation'
  | 'ptbf-rolling'
  | 'cs-fixation'
  | 'local-p-plans'
  | 'ref-ValueUpdates'
  | 'ref-FuturesList'
  | 'ref-CP-List'
  | 'ref-Delivery-terms'
  | 'ref-Futures'
  | 'ref-Bank'
  | 'ref-License'
  | 'ref-FuturesLotAdjust'
  | 'ref-ListAndValo'
  | 'ref-Package'
  | 'ref-Delivery-Place'
  | 'ref-Certification'

export interface PurchaseRepInfoItem {
  id: number
  name?: string | null
  position?: string | null
}

export interface PurchaseRepInfoResponse {
  items: PurchaseRepInfoItem[]
  currentSignId?: number | null
}

export interface PurchaseContractListFiltersPatch {
  deliveryLatestFrom?: string
  deliveryLatestTo?: string
  isForcedClose?: 'all' | 'yes' | 'no'
}

export type PurchaseReferenceRow = Record<string, unknown>

export interface PurchaseReferenceRowsQuery {
  pageKey: string
  limit?: number
}

export interface PurchaseReferencePageResponse {
  pageKey: string
  sourceForm: string
  sourceQuery: string
  items: PurchaseReferenceRow[]
  total: number
}

export const purchaseContractApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPurchaseContracts: builder.query<PurchaseContractListResponse, PurchaseContractListQuery>({
      query: ({ page, pageSize, q, status, supplierCode, contractType, certification }) => ({
        url: '/purchase-contracts',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(status ? { status } : {}),
          ...(supplierCode ? { supplierCode } : {}),
          ...(contractType ? { contractType } : {}),
          ...(certification ? { certification } : {}),
        },
      }),
      providesTags: ['User'],
    }),
    createPurchaseContract: builder.mutation<PurchaseContract, PurchaseContractPayload>({
      query: (body) => ({
        url: '/purchase-contracts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    updatePurchaseContract: builder.mutation<PurchaseContract, { id: string; body: PurchaseContractPayload }>({
      query: ({ id, body }) => ({
        url: `/purchase-contracts/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    cancelPurchaseContract: builder.mutation<PurchaseContract, string>({
      query: (id) => ({
        url: `/purchase-contracts/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['User'],
    }),
    getPurchaseReferenceRows: builder.query<PurchaseReferencePageResponse, PurchaseReferenceRowsQuery>({
      query: ({ pageKey, limit = 1000000 }) => ({
        url: `/purchase-contracts/references/${encodeURIComponent(pageKey)}`,
        params: { limit },
      }),
    }),
    getPurchaseContractPagePreferences: builder.query<PurchaseContractPagePreferences, void>({
      query: () => ({ url: '/purchase-contracts/page-preferences' }),
      providesTags: ['PurchaseContractPrefs'],
    }),
    getPurchaseContractColumnsPreference: builder.query<PurchaseContractColumnsPreference, PurchaseContractColumnsMode>({
      query: (mode) => ({ url: `/purchase-contracts/preferences/columns/${encodeURIComponent(mode)}` }),
      providesTags: (_r, _e, mode) => [{ type: 'PurchaseContractPrefs', id: `columns:${mode}` }],
    }),
    putPurchaseContractColumnsPreference: builder.mutation<
      { message: string },
      { mode: PurchaseContractColumnsMode; columns: string[]; defaultColumns?: string[] }
    >({
      query: ({ mode, columns, defaultColumns }) => ({
        url: `/purchase-contracts/preferences/columns/${encodeURIComponent(mode)}`,
        method: 'PUT',
        body: { columns, ...(defaultColumns ? { defaultColumns } : {}) },
      }),
      invalidatesTags: (_r, _e, { mode }) => [{ type: 'PurchaseContractPrefs', id: `columns:${mode}` }],
    }),
    getPurchaseRepInfo: builder.query<PurchaseRepInfoResponse, void>({
      query: () => ({ url: '/purchase-contracts/rep-info' }),
      providesTags: ['PurchaseContractPrefs'],
    }),
    patchPurchaseContractListFilters: builder.mutation<{ message: string }, PurchaseContractListFiltersPatch>({
      query: (body) => ({
        url: '/purchase-contracts/list-filters',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['PurchaseContractPrefs'],
    }),
    setPurchaseRepInfo: builder.mutation<{ message: string }, number>({
      query: (signId) => ({
        url: '/purchase-contracts/rep-info',
        method: 'POST',
        body: { signId },
      }),
      invalidatesTags: ['PurchaseContractPrefs'],
    }),
  }),
})

export const {
  useGetPurchaseContractsQuery,
  useCreatePurchaseContractMutation,
  useUpdatePurchaseContractMutation,
  useCancelPurchaseContractMutation,
  useGetPurchaseReferenceRowsQuery,
  useGetPurchaseContractPagePreferencesQuery,
  useGetPurchaseContractColumnsPreferenceQuery,
  usePutPurchaseContractColumnsPreferenceMutation,
  useGetPurchaseRepInfoQuery,
  usePatchPurchaseContractListFiltersMutation,
  useSetPurchaseRepInfoMutation,
} = purchaseContractApi
